import { isNumber, isString } from 'fitify-types/src/type-guards/common'
import { parseInteger, toBoolean } from 'fitify-utils/src/properties'
import { useCallback, useMemo } from 'react'

import {
  type UseGrowthBook,
  useGrowthBook,
} from '../../utils/growthbook/generated'

type GetParameterArgs = {
  promoKey?: string
  defaultKey?: string
  shouldUseDefaultParam?: boolean
}

type GetGrowthBookParameterFactoryArgs = {
  growthBook: UseGrowthBook
  promoCode?: string
}

type GetParameterValueArgs = {
  defaultValue: unknown
  promoCode?: string
  promoKey?: string
  promoValue: unknown
  shouldUseDefaultParam?: boolean
}

const getParameterValue = ({
  defaultValue,
  promoCode,
  promoKey,
  promoValue,
  shouldUseDefaultParam,
}: GetParameterValueArgs) => {
  if (promoCode && promoKey) {
    if (typeof promoValue !== 'undefined') {
      return promoValue
    } else if (shouldUseDefaultParam) {
      return defaultValue
    } else {
      return undefined
    }
  }

  return defaultValue
}

const getGrowthBookParameterFactory =
  ({ growthBook, promoCode }: GetGrowthBookParameterFactoryArgs) =>
  ({ promoKey, defaultKey, shouldUseDefaultParam }: GetParameterArgs): any => {
    const defaultValue = defaultKey
      ? (growthBook.evalFeature(defaultKey)?.value ?? undefined)
      : undefined
    const promoValue = promoKey
      ? (growthBook.evalFeature(promoKey)?.value ?? undefined)
      : undefined

    return getParameterValue({
      defaultValue,
      promoCode,
      promoKey,
      promoValue,
      shouldUseDefaultParam,
    })
  }

export type GrowthBookParameters = {
  getBooleanParameter: (args: GetParameterArgs) => boolean | undefined
  getIntegerParameter: (args: GetParameterArgs) => number | undefined
  getStringParameter: (args: GetParameterArgs) => string | undefined
}

export const useGrowthBookParameters = (
  promoCode?: string
): GrowthBookParameters => {
  const growthBook = useGrowthBook()

  const getParameter = useMemo(
    () => getGrowthBookParameterFactory({ growthBook, promoCode }),
    [growthBook, promoCode]
  )

  const getBooleanParameter = useCallback(
    (args: GetParameterArgs): boolean | undefined => {
      return toBoolean(getParameter(args))
    },
    [getParameter]
  )

  const getIntegerParameter = useCallback(
    (args: GetParameterArgs): number | undefined => {
      const parameter = getParameter(args)
      if (isNumber(parameter)) {
        return parameter
      }
      if (isString(parameter)) {
        return parseInteger(parameter) ?? undefined
      }
      return undefined
    },
    [getParameter]
  )

  const getStringParameter = useCallback(
    (args: GetParameterArgs): string | undefined => {
      const parameter = getParameter(args)
      if (isString(parameter) && parameter !== '') {
        return parameter
      }
      return undefined
    },
    [getParameter]
  )

  return { getBooleanParameter, getIntegerParameter, getStringParameter }
}
